import React, { PropTypes } from 'react';
import { browserHistory } from 'react-router';
import _ from 'lodash';
import moment from 'moment';
import uuid from 'uuid';
import processLinks from 'utils/embedly';
import { replaceUnicode, replaceShortcodes } from 'utils/emojify';
import { shareOnFacebook } from 'utils/share';
import parseAddressComponents from 'utils/parseAddressComponents';
import Helmet from 'react-helmet';
import DateTime from 'react-datetime';
import Autocomplete from 'components/places/autocomplete';
import Loading from 'components/helpers/loading';
import PhotoList from './photoList';

class Checkin extends React.Component {

    initialState = {
        isDraft: false,
        summary: '',
        date: moment(),
        description: '',
        place: null,
        doShare: true,
        previewPhotoIndex: null
    };

    constructor(props) {
        super(props);

        var self = this,
            isLoading = false;

        if (self.props.params.id && self.props.existingCheckin == null) {
            isLoading = true;
            self.props.getAllCheckins();
        }
        
        self.state = {
            ...self.initialState,
            id: self.props.params.id || uuid(),
            isLoading: isLoading
        };

        self.geocoder = new google.maps.Geocoder();
    }

    componentWillReceiveProps(nextProps) {
        var self = this,
            checkin;

        if (!nextProps.params.id && !self.state.isLoading) {
            // Only clear all fields if we're not saving a new checkin or loading an existing one
            self.setState({
                ...self.initialState,
                id: uuid(),
                isLoading: false
            });
        }
        else if (self.state.isLoading && nextProps.existingCheckin !== null) {
            // Successfully retrieved existing checkin, populate form
            checkin = nextProps.existingCheckin;

            self.setState({
                summary: checkin.summary,
                date: moment(checkin.date),
                description: checkin.description,
                place: checkin.place,
                selectedPhotoIndex: checkin.selectedPhotoIndex,
                doShare: checkin.isDraft,
                isLoading: false
            });
        }
    }

    onPlaceChanged = (place) => {
        var self = this;

        self.setState({
           place: place,
           selectedPhotoIndex: 0
        });
    }

    onSaveDraft = (e) => {
        var self = this;

        self.setState({
            isDraft: true
        }, () => {
            self.onFormSubmit(e);
        });
    }

    onDelete = (e) => {
        var self = this;

        if (!window.confirm('Are you sure you want to delete this entry?')) {
            return;
        }

        self.props.deleteCheckin(self.props.params.id).then(data => {
            if (data.payload.errorCode) {
                self.setState({
                    errorMessage: data.payload.errorMessage,
                    isLoading: false
                });
            }

            browserHistory.push('/journal');
        }).catch(error => {
            self.setState({
                errorMessage: 'Delete failed, please try again. ' + error.message,
                isLoading: false
            });
        });
    }

    onFormSubmit = (e) => {
        var self = this,
            description,
            textDescription,
            doShare = self.state.doShare,
            isDraft = self.state.isDraft,
            id = self.state.id;

        e.preventDefault();

        if (self.state.summary == null || self.state.summary === '') {
            self.setState({
                errorMessage: 'Please provide a summary.'
            });
            return;
        }
        else if (self.state.place == null) {
            self.setState({
                errorMessage: 'Please select a location.'
            });
            return;
        }

        self.setState({
            isLoading: true
        });

        description = replaceUnicode(self.state.description);

        processLinks(description).then(html => {
            html = replaceShortcodes(html);

            return self.props.checkin({
                id: id,
                summary: self.state.summary,
                date: self.state.date.toISOString(),
                description: description || null,
                html: html,
                place: self.state.place,
                selectedPhotoIndex: self.state.selectedPhotoIndex || null,
                isDraft: isDraft
            });
        }).then(data => {
            if (data.payload.errorCode) {
                self.setState({
                    errorMessage: data.payload.errorMessage,
                    isLoading: false
                });
            }
            else if (!isDraft && doShare) {
                shareOnFacebook(data.payload.find(c => c.id === id));

                browserHistory.push('/journal');
            }
            else {
                browserHistory.push('/journal');
            }
        }).catch(error => {
            self.setState({
                errorMessage: 'Save failed, please try again. ' + error.message,
                isLoading: false
            });
        });
    }

    onSummaryChanged = () => {
        var self = this;

        self.setState({
            summary: self.refs.summary.value
        });
    }

    onDateChanged = (date) => {
        var self = this;

        self.setState({
            date: date
        });
    }

    onDescriptionChanged = () => {
        var self = this;

        self.setState({
            description: self.refs.description.value
        });
    }

    render () {
        var self = this,
            photos;

        if (self.state.isLoading) {
            return <Loading />;
        }

        return (
            <div className="page-container page-container-responsive">
                <Helmet title="Walkabout: Check-In" />
                <h1 className="page-title">Check-in</h1>
                
                <form className="page-content" onSubmit={(e) => e.preventDefault()}>
                    <div className="form-group">
                        <label htmlFor="summary">Summary</label>
                        <input type="text" maxLength="150" className="form-control" id="summary" aria-describedby="summaryHelp" placeholder="ex. Glacier Hike!" ref="summary" value={ self.state.summary } onChange={ self.onSummaryChanged } autoFocus={ true } />
                        <small id="summaryHelp" className="form-text text-muted">Enter a summary of what's happening now</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="date">Date</label>
                        <DateTime value={ self.state.date } id="date" aria-describedby="dateHelp" onChange={ self.onDateChanged } />
                        <small id="dateHelp" className="form-text text-muted">Published date of this post</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="location">Location</label>
                        <Autocomplete id="location" aria-describedby="locationHelp" placeholder="ex. Franz Josef Glacier" requireResolvedPlace={ true } defaultPlace={ self.state.place } onChange={ self.onPlaceChanged } />
                        <small id="tripTitleHelp" className="form-text text-muted">Where are you now? Type to pick a location, then select a photo below if available.</small>
                        <PhotoList place={ self.state.place }
                                   selectedPhotoIndex={ self.state.selectedPhotoIndex }
                                   onPhotoSelected={ (i) => self.setState({ selectedPhotoIndex: i }) }>
                            <small className="form-text text-muted text-primary highlight">The location that you've selected has no photos, just FYI.</small>
                        </PhotoList>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea rows="6" className="form-control" id="desription" aria-describedby="descriptionHelp" placeholder="Go ahead, tell your story." ref="description" value={ self.state.description } onChange={ self.onDescriptionChanged } />
                        <small id="descriptionHelp" className="form-text text-muted">Describe all the juicy details</small>
                    </div>

                    <div className="form-group checkin-actions">
                        <div className="btn-group dropup">
                            <button className="btn btn-primary" ref="submit" onClick={ self.onFormSubmit } disabled={ self.state.isLoading }><i className="fa fa-paper-plane-o"></i>  Send Update</button>
                            <button type="button" className="btn btn-primary dropdown-toggle dropdown-toggle-split" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                <span className="sr-only">Toggle Dropdown</span>
                            </button>

                            <div className="dropdown-menu">
                                <a className="dropdown-item" href="javascript:void(0)" onClick={ self.onSaveDraft } disabled={ self.state.isLoading }>Save as Draft</a>
                                { self.props.params.id ? <div className="dropdown-divider"></div> : null }
                                { self.props.params.id ? <a className="dropdown-item" href="javascript:void(0)" onClick={ self.onDelete } disabled={ self.state.isLoading }>Delete</a> : null }
                            </div>
                        </div>
                        <label className="custom-control custom-checkbox">
                            <input type="checkbox" className="custom-control-input" checked={ self.state.doShare } onChange={() => self.setState({ doShare: !self.state.doShare })} />
                            <span className="custom-control-indicator"></span>
                            <span className="custom-control-description"><i className="fa fa-facebook-official"></i> Share</span>
                        </label>

                        { self.state.errorMessage != null ? <div style={ { color: 'red' }}>{ self.state.errorMessage }</div> : null }
                    </div>
                </form>
            </div>
        );
    }
};

export default Checkin;


// WEBPACK FOOTER //
// ./src/app/components/checkin/checkin.jsx